import { useState, type FormEvent } from "react";
import { api, type Me } from "../api";
import { AuthCard, Layout, Loading } from "../components/Layout";
import { Checkbox, Field, FormError, SubmitButton } from "../components/Form";
import { config } from "../config";
import { pendingDesktopRequest } from "../desktop";
import { errorMessage } from "../messages";
import { Redirect, useRouter } from "../router";
import { homeFor, useSession } from "../session";
import { formatIsraeliPhone, normalizeFullName, normalizeIsraeliPhone } from "../validation";

type Errors = Partial<Record<"fullName" | "phone" | "terms", string>>;

/** Details and terms still missing, e.g. after signing in with Google or a sign-up that was interrupted. */
export function CompleteProfile() {
  const { session, refresh } = useSession();

  if (session.status === "loading") {
    return <Loading />;
  }
  if (session.status === "signed-out") {
    return <Redirect to="/login" />;
  }
  if (!session.me) {
    return session.error ? (
      <Layout>
        <AuthCard title="לא הצלחנו לטעון את החשבון">
          <FormError message={session.error} />
          <button type="button" className="button button-primary" onClick={() => void refresh()}>
            ניסיון נוסף
          </button>
        </AuthCard>
      </Layout>
    ) : (
      <Loading />
    );
  }
  // Mounted once the account is known, so the form starts with its details.
  return <ProfileForm me={session.me} displayName={session.user.displayName} />;
}

function ProfileForm({ me, displayName }: { me: Me; displayName: string | null }) {
  const { token, refresh } = useSession();
  const { navigate } = useRouter();
  const [fullName, setFullName] = useState(me.fullName ?? displayName ?? "");
  const [phone, setPhone] = useState(me.phone ? formatIsraeliPhone(me.phone) : "");
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(me.marketingOptIn);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (me.profileComplete && me.termsAccepted && !busy) {
    return <Redirect to={homeFor(me)} />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const found: Errors = {};
    if (!normalizeFullName(fullName)) found.fullName = "נדרש שם מלא: שם פרטי ושם משפחה.";
    if (!normalizeIsraeliPhone(phone)) found.phone = "נדרש מספר טלפון ישראלי, למשל 050-1234567.";
    if (!me.termsAccepted && !terms) found.terms = "כדי להמשיך צריך לאשר את תנאי השימוש ואת מדיניות הפרטיות.";
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    setBusy(true);
    try {
      const idToken = await token();
      await api.saveProfile(idToken, { fullName, phone, marketingOptIn: marketing });
      if (!me.termsAccepted) {
        await api.acceptTerms(idToken, me.termsVersion);
      }
      await refresh();
      navigate(pendingDesktopRequest() ? "/desktop" : "/account", { replace: true });
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <AuthCard
        title="עוד כמה פרטים"
        subtitle={
          me.email ? (
            <>
              כמעט סיימנו, <bdi dir="ltr">{me.email}</bdi>.
            </>
          ) : (
            "כמעט סיימנו."
          )
        }
      >
        <form onSubmit={(event) => void submit(event)} noValidate>
          <Field
            label="שם מלא"
            value={fullName}
            onChange={setFullName}
            autoComplete="name"
            placeholder="שם פרטי ושם משפחה"
            error={errors.fullName}
          />
          <Field
            label="טלפון"
            type="tel"
            dir="ltr"
            value={phone}
            onChange={setPhone}
            autoComplete="tel"
            inputMode="tel"
            placeholder="050-1234567"
            hint="כדי שנוכל לחזור אליכם להפעלת החשבון ולתמיכה."
            error={errors.phone}
          />
          {!me.termsAccepted && (
            <Checkbox checked={terms} onChange={setTerms} error={errors.terms}>
              קראתי ואני מאשר/ת את{" "}
              <a href={config.termsUrl} target="_blank" rel="noopener">
                תנאי השימוש
              </a>{" "}
              ואת{" "}
              <a href={config.privacyUrl} target="_blank" rel="noopener">
                מדיניות הפרטיות
              </a>
              .
            </Checkbox>
          )}
          <Checkbox checked={marketing} onChange={setMarketing}>
            אשמח לקבל מ-FrameFlow עדכונים, טיפים והצעות באימייל, ב-SMS ובוואטסאפ. אפשר לבטל בכל עת.
          </Checkbox>
          <FormError message={formError} />
          <SubmitButton busy={busy}>שמירה והמשך</SubmitButton>
        </form>
      </AuthCard>
    </Layout>
  );
}
